import { convertDateToISOString } from '@ms/odsp-utilities/lib/dateTime/DateTime';
import { ItemUrlHelper } from '../../utilities/url/ItemUrlHelper';
import { getUserPhotoUrl } from '../../utilities/url/UserPhotoUrlHelper';
import { ApiUrlHelper, IApiUrl } from '../../utilities/url/ApiUrlHelper';
import DataSource, { IDataSourceDependencies } from '../base/DataSource';
import FeatureOverrides from '@ms/odsp-utilities/lib/features/FeatureOverrides';
import { ISPListItem } from '../item/spListItemProcessor/ISPListItemData';
import { IPerson } from '../../dataSources/peoplePicker/IPerson';
import {
    IShareResult, ISharingContext, IShareLinkContext, IShareLinkResult,
    IShareLinkSettings, IUnshareLinkContext, IUpdatePermissionsResponse,
    IRecipient, SharingRole, ISharingInfo, ClientId,
    ISharingInformation, ISharingLinkV3, ISharingPrincipal, SharingAudience,
    SharingLinkKind as SharingLinkKindV3, ISharingItemInformation, SharingRoleV3,
    Error, IGetSharingInformationResponse, ISharingLinkFromAPI, Mode,
    ISharingPrincipalFromAPI, ISharingContextInformation, SharingLinkType,
    ISharingLinkAbilitiesMatrix, ISharingLinkTemplate, ISharingLinkTemplateFromAPI
} from '../../interfaces/sharing/SharingInterfaces';
import { getSafeWebServerRelativeUrl } from '../../interfaces/ISpPageContext';
import { ISharingDataSource, ISharingDataSourceParams } from './ISharingDataSource';
import ItemType from '@ms/odsp-utilities/lib/icons/ItemType';
import Promise, { ICancelablePromise } from '@ms/odsp-utilities/lib/async/Promise';
import SharingDataSourceHelper from './SharingDataSourceHelper';
import { PolicyTipType } from '../item/spListItemProcessor/SPListItemEnums';
import WebTemplateType from '../web/WebTemplateType';

export const EDIT_ROLE = 'role:1073741827';
export const VIEW_ROLE = 'role:1073741826';
export const UseShareLinkTemplates = { ODB: 1205 };

export class SharingDataSource extends DataSource implements ISharingDataSource {
    private _getParentListId: (item: ISPListItem) => string;
    private _itemUrlHelper: ItemUrlHelper;
    private _apiUrlHelper: ApiUrlHelper;
    private _shareUrl: string;
    private _sharingContextInformation: ISharingContextInformation;
    private _sharingDataSourceHelper: SharingDataSourceHelper;

    constructor(params: ISharingDataSourceParams, dependencies: IDataSourceDependencies = {
        pageContext: params.pageContext
    }) {
        super({
            dataSourceName: 'SharingDataSource'
        }, dependencies);

        this._getParentListId = params.getParentListId;
        this._itemUrlHelper = params.itemUrlHelper || new ItemUrlHelper({}, { pageContext: params.pageContext });
        this._apiUrlHelper = params.apiUrlHelper || new ApiUrlHelper({}, { pageContext: params.pageContext, itemUrlHelper: this._itemUrlHelper });
        this._sharingContextInformation = params.sharingContextInformation || undefined;
        this._sharingDataSourceHelper = new SharingDataSourceHelper(
            {
                itemUrlHelper: this._itemUrlHelper,
                sharingContextInformation: this._sharingContextInformation,
                isItemUrlEncoded: params.isItemUrlEncoded
            }
        );
        let webServerRelativeUrl = getSafeWebServerRelativeUrl(this._pageContext);
        this._shareUrl = webServerRelativeUrl + '/_api/SP.Web.ShareObject';
    }

    public static parsePeople(results: Array<any>): Array<IPerson> {
        return results.map((result: any): IPerson => {
            let sharingInfo = <ISharingInfo>{
                role: SharingDataSource.parseSharingRole(result.CurrentRole),
                statusCode: Number(!result.Status), // ODB always returns a bool here and we want it to be a number
                errorMessage: result.Message || null
            };

            return {
                name: result.DisplayName,
                email: result.Email,
                userId: result.UserId,
                sharingInfo: sharingInfo
            };
        });
    }

    public static parseSharingRole(role: number) {
        switch (role) {
            case 0:
                return SharingRole.None;
            case 1:
                return SharingRole.View;
            case 2:
                return SharingRole.Edit;
            case 6:
                return SharingRole.Review;
            default:
                return SharingRole.View;
        }
    }

    // Add expand link template boolean to the method call. We always want picker settings, expandPermissions and expandLink are independent
    // Once we get the template, we need to parse it, then send the relevant info to password stuff
    public getSharingInformation(item: ISPListItem, expandPermissionsInformation?: boolean, sharingLinkTemplates?: boolean, baseUrl?: IApiUrl): Promise<ISharingInformation> {
        baseUrl = baseUrl || this._sharingDataSourceHelper.getApiRoot(this._apiUrlHelper, item, this._getParentListId(item));
        const getSharingInformationUrlBuilder = baseUrl.segment('GetSharingInformation');
        /**
         * If we're using the sharing abilitites matrix, we don't need to request
         * "permissionsInformation", which will improve the perf.
         */
        let getSharingInformationUrl;
        const useShareLinkTemplates = FeatureOverrides.isFeatureEnabled(UseShareLinkTemplates);
        // Flighting
        if (!useShareLinkTemplates) {
            if (!expandPermissionsInformation) {
                getSharingInformationUrl = getSharingInformationUrlBuilder
                    .oDataParameter('$Expand', 'pickerSettings')
                    .toString();
            } else {
                getSharingInformationUrl = getSharingInformationUrlBuilder
                    .oDataParameter('$Expand', 'permissionsInformation,pickerSettings')
                    .toString();
            }
        }
        // Now with sharingLinkTemplates
        else {
            let sharingInformationSettings: string = 'pickerSettings';
            if (expandPermissionsInformation) {
                sharingInformationSettings += ',permissionsInformation';
            }
            if (sharingLinkTemplates) {
                sharingInformationSettings += ',sharingLinkTemplates';
            }
            getSharingInformationUrl = getSharingInformationUrlBuilder
                .oDataParameter('$Expand', sharingInformationSettings)
                .toString();
        }

        return this.getData<ISharingInformation>(
            /* getUrl */(): string => getSharingInformationUrl,
            /* parseResponse */(responseText: string): ISharingInformation => {
                const response: IGetSharingInformationResponse = JSON.parse(responseText);
                const sharingInformation: ISharingInformation = this._parseGetSharingInformationResponse(response, item);
                return sharingInformation;
            },
            /* qosName */ 'GetSharingInformation',
            /* getAdditionalPostData */ null,
            /* method */ 'POST',
            /* additionalHeaders */ null,
            /* contentType */ null,
            /* maxRetries */ null,
            /* noRedirect */ true,
            /* crossSiteCollectionCall */ undefined,
            /* qosHandler */ undefined,
            /* customQosLogger */ undefined,
            /* qosExtraData */ this._getExtraData()
        );
    }

    public emailWithoutPermissioning(recipients: Array<IPerson>, item: ISPListItem, message?: string, subject?: string): Promise<IShareResult> {
        const forwardObjectLinkUrl = this._sharingDataSourceHelper.getApiRoot(this._apiUrlHelper, item, this._getParentListId(item))
            .segment('ForwardObjectLink')
            .toString();

        return this.getData<IShareResult>(
                /* getUrl */(): string => forwardObjectLinkUrl,
                /* parseResponse */(responseText: string): any => {
                let response: any = JSON.parse(responseText);
                let data: any = response.d;
                let results: Array<any> = [];
                if (data.UniquelyPermissionedUsers && data.UniquelyPermissionedUsers.results) {
                    results = data.UniquelyPermissionedUsers.results;
                }

                let invitedUsers: Array<any> = [];
                if (data.InvitedUsers && data.InvitedUsers.results) {
                    invitedUsers = data.InvitedUsers.results;
                }

                let shareResult = <IShareResult>{};
                shareResult.statusCode = data.StatusCode;
                shareResult.errorMessage = data.ErrorMessage;
                shareResult.people = SharingDataSource.parsePeople(results);
                shareResult.invitedUsers = SharingDataSource.parsePeople(invitedUsers);
                shareResult.itemId = item && item.id;

                // If there are any invitees, grab the invitation link.
                if (invitedUsers.length > 0) {
                    shareResult.invitationLink = invitedUsers[0].InvitationLink;
                }

                // If there are any users with invalid emails, extract them.
                shareResult.invalidEmails = [];
                if (data.UsersWithInvalidEmails && data.UsersWithInvalidEmails.results) {
                    for (const user of data.UsersWithInvalidEmails.results) {
                        shareResult.invalidEmails.push(user.User);
                    }
                }

                return shareResult;
            },
            /* qosName */ 'ForwardObjectLink',
            /* getAdditionalPostData */(): string => {
                let peoplePickerSchema: Array<any> = [];
                for (const p of recipients) {
                    peoplePickerSchema.push(p.rawPersonData);
                }

                let body = {
                    peoplePickerInput: JSON.stringify(peoplePickerSchema),
                    emailBody: message,
                    emailSubject: subject
                };

                return JSON.stringify(body);
            }
        );
    }

    public checkPermissions(recipients: Array<IRecipient>, item: ISPListItem): Promise<any> {
        const checkPermissionsUrl = this._sharingDataSourceHelper.getApiRoot(this._apiUrlHelper, item, this._getParentListId(item))
            .segment('CheckPermissions')
            .toString();

        return this.getData<any>(
            /* getUrl */(): string => checkPermissionsUrl,
            /* parseResponse */(responseText: string): any => {
                const response: any = JSON.parse(responseText);

                if (response.d) {
                    const data = response.d.CheckPermissions;
                    return data.results;
                } else {
                    return null;
                }
            },
            /* qosName */ 'CheckPermissions',
            /* getAdditionalPostData */(): string => {
                let body = {
                    recipients: recipients
                };

                return JSON.stringify(body);
            }
        );
    }

    public share(people: Array<IPerson>, context: ISharingContext<ISPListItem>): Promise<IShareResult> {
        const canUseSecurable = !!this._sharingContextInformation || this._sharingDataSourceHelper.canUseSecurable(context.items[0]);
        if (context.linkType === SharingLinkType.Organization || context.linkType === SharingLinkType.Edit || context.linkType === SharingLinkType.View) {
            return this._shareLink(people, context);
        } else {
            /**
             * Use new securable sharing API if item has necessary properties to build the
             * API root, otherwise, fall back to the web API.
             */
            let shareUrl: string;
            if (canUseSecurable) {
                let item: ISPListItem = context.items[0];
                let shareUrlBuilder = this._sharingDataSourceHelper.getApiRoot(this._apiUrlHelper, item, this._getParentListId(item));

                shareUrl = shareUrlBuilder
                    .segment('ShareObject')
                    .toString();

            } else {
                shareUrl = this._shareUrl;
            }

            return this.getData<IShareResult>(
                /* getUrl */(): string => shareUrl,
                /* parseResponse */(responseText: string): any => {
                    let response: any = JSON.parse(responseText);
                    let data: any = response.d;
                    let results: Array<any> = [];
                    if (data.UniquelyPermissionedUsers && data.UniquelyPermissionedUsers.results) {
                        results = data.UniquelyPermissionedUsers.results;
                    }

                    let invitedUsers: Array<any> = [];
                    if (data.InvitedUsers && data.InvitedUsers.results) {
                        invitedUsers = data.InvitedUsers.results;
                    }

                    let shareResult = <IShareResult>{};
                    shareResult.statusCode = data.StatusCode;
                    shareResult.errorMessage = data.ErrorMessage;
                    shareResult.people = SharingDataSource.parsePeople(results);
                    shareResult.invitedUsers = SharingDataSource.parsePeople(invitedUsers);
                    shareResult.itemId = context.items[0] && context.items[0].id;

                    // If there are any invitees, grab the invitation link.
                    if (invitedUsers.length > 0) {
                        shareResult.invitationLink = invitedUsers[0].InvitationLink;
                    }

                    // If there are any users with invalid emails, extract them.
                    shareResult.invalidEmails = [];
                    if (data.UsersWithInvalidEmails && data.UsersWithInvalidEmails.results) {
                        for (const user of data.UsersWithInvalidEmails.results) {
                            shareResult.invalidEmails.push(user.User);
                        }
                    }

                    return shareResult;
                },
                /* qosName */ 'Share',
                /* getAdditionalPostData */(): string => {
                    let peoplePickerSchema: Array<any> = [];
                    for (let i = 0; i < people.length; i++) {
                        let p: IPerson = people[i];
                        peoplePickerSchema.push(p.rawPersonData);
                    }

                    // Convert SharingRole value to value understood by the share API.
                    if (context.roleValue === SharingRole.Edit.toString()) {
                        context.roleValue = EDIT_ROLE;
                    } else if (context.roleValue === SharingRole.View.toString()) {
                        context.roleValue = VIEW_ROLE;
                    }

                    let strBody = {
                        peoplePickerInput: JSON.stringify(peoplePickerSchema),
                        roleValue: context.roleValue,
                        sendEmail: context.sendEmail,
                        emailBody: context.message,
                        emailSubject: context.subject,
                        includeAnonymousLinkInEmail: !(context.requireSignIn),
                        propagateAcl: context.propagateSharing,
                        useSimplifiedRoles: true
                    };

                    // If using old sharing APIs, add url parameter to payload.
                    if (!canUseSecurable) {
                        strBody['url'] = this._sharingDataSourceHelper.getFileUrl(context.items[0]);
                    }

                    return JSON.stringify(strBody);
                }
            );
        }
    }

    public updatePermissions2(person: any, item: ISPListItem, role: SharingRoleV3): Promise<any> {
        const updatePermissionsUrlBuilder = this._sharingDataSourceHelper.getApiRoot(this._apiUrlHelper, item, this._getParentListId(item));
        const updatePermissionsUrl = updatePermissionsUrlBuilder
            .segment('UpdateDocumentSharingInfo')
            .toString();

        return this.getData<any>(
            /* getUrl */(): string => updatePermissionsUrl,
            /* parseResponse */(responseText: string): any => {
                const response: any = JSON.parse(responseText);
                const data: any = response.d;

                if (!data) {
                    return null;
                }

                const updatePermissionsResponse: IUpdatePermissionsResponse = {
                    role: data.UpdateDocumentSharingInfo.results[0].CurrentRole
                };

                return updatePermissionsResponse;
            },
            /* qosName */ 'UpdatePermissions2',
            /* getAdditionalPostData */(): string => {
                let newRole: number;
                switch (role) {
                    case SharingRoleV3.none:
                        newRole = 0; /* stop sharing */
                        break;
                    case SharingRoleV3.view:
                        newRole = 1;
                        break;
                    case SharingRoleV3.review:
                        newRole = 6;
                        break;
                    case SharingRoleV3.edit:
                        newRole = 2;
                        break;
                    default:
                        newRole = 1;
                        break;
                }

                const userRoles: Array<any> = [{
                    UserId: person.loginName,
                    Role: newRole
                }];

                const body = {
                    userRoleAssignments: userRoles,
                    validateExistingPermissions: false,
                    additiveMode: false,
                    sendServerManagedNotification: false,
                    customMessage: null,
                    includeAnonymousLinksInNotification: false,
                    propagateAcl: true
                };

                return JSON.stringify(body);
            }
        );
    }

    // Update permissions for many people
    public updatePermissionsMultiple(people: Array<IPerson>, item: ISPListItem, role: SharingRoleV3, notify: boolean, message?: string): ICancelablePromise<IUpdatePermissionsResponse> {
        const updatePermissionsUrlBuilder = this._sharingDataSourceHelper.getApiRoot(this._apiUrlHelper, item, this._getParentListId(item));
        const updatePermissionsUrl = updatePermissionsUrlBuilder
            .segment('UpdateDocumentSharingInfo')
            .toString();

        return this.getData<IUpdatePermissionsResponse>(
            /* getUrl */(): string => updatePermissionsUrl,
            /* parseResponse */(responseText: string): any => {
                const response: any = JSON.parse(responseText);
                const data: any = response.d;

                if (!data) {
                    return null;
                }

                const updatePermissionsResponse: IUpdatePermissionsResponse = {
                    role: data.UpdateDocumentSharingInfo.results[0].CurrentRole,
                    success: data.UpdateDocumentSharingInfo.results.map(x => x.Status).reduce((x, y) => x && y, true)   // Check that status is true for all the returned results
                };

                return updatePermissionsResponse;
            },
            /* qosName */ 'UpdatePermissionsMultiple',
            /* getAdditionalPostData */(): string => {
                let newRole: number;
                switch (role) {
                    case SharingRoleV3.none:
                        newRole = 0; /* stop sharing */
                        break;
                    case SharingRoleV3.view:
                        newRole = 1;
                        break;
                    case SharingRoleV3.review:
                        newRole = 6;
                        break;
                    case SharingRoleV3.edit:
                        newRole = 2;
                        break;
                    default:
                        newRole = 1;
                        break;
                }

                const userRoles = people.map(person => { return { UserId: person.userId, Role: newRole }; });

                const body = {
                    userRoleAssignments: userRoles,
                    validateExistingPermissions: false,
                    additiveMode: false,
                    sendServerManagedNotification: notify,
                    customMessage: message,
                    includeAnonymousLinksInNotification: false,
                    propagateAcl: true
                };

                return JSON.stringify(body);
            }
        );
    }

    public parseRawSharingInformation(rawSharingInformation: IGetSharingInformationResponse): ISharingInformation {
        return this._parseGetSharingInformationResponse(rawSharingInformation, undefined);
    }

    public unshareLink(context: IUnshareLinkContext<ISPListItem>): Promise<boolean> {
        const deleteLinkUrl = this._sharingDataSourceHelper.getApiRoot(this._apiUrlHelper, context.item, this._getParentListId(context.item))
            .segment('UnshareLink')
            .toString();

        return this.getData<boolean>(
            /* getUrl */(): string => deleteLinkUrl,
            /* parseResponse */(responseText: string): boolean => {
                const response: any = JSON.parse(responseText);
                const unshareLinkResponse: boolean = !!response.d;

                // If unshareLinkResponse is true, unshare was successful.
                return unshareLinkResponse;
            },
            /* qosName */ 'UnshareLink',
            /* getAdditionalPostData */(): string => {
                let body = {
                    linkKind: context.kind,
                    shareId: context.shareId
                };

                return JSON.stringify(body);
            }
        );
    }

    public shareLink(context: IShareLinkContext<ISPListItem>): Promise<IShareLinkResult> {
        const shareLinkSettings: IShareLinkSettings = context.settings;
        const item: ISPListItem = context.items[0];
        const shareLinkUrl = this._sharingDataSourceHelper.getApiRoot(this._apiUrlHelper, context.items[0], this._getParentListId(item))
            .segment('ShareLink')
            .toString();
        return this.getData<IShareLinkResult>(
            /* getUrl */(): string => shareLinkUrl,
            /* parseResponse */(responseText: string): any => {
                const fullResponse: any = JSON.parse(responseText);

                if (fullResponse.d) {
                    return parseSharingLinkInfo(fullResponse);
                } else if (fullResponse.error) {
                    return {
                        error: Error.generic,
                        errorMessage: fullResponse.error.message
                    };
                }
            },
            /* qosName */ 'ShareLink',
            /* getAdditionalPostData */(): string => {
                // Get expiration value.
                let expirationString = null;
                if (shareLinkSettings.expiration) {
                    expirationString = convertDateToISOString(shareLinkSettings.expiration.toString());
                }

                // Get peoplePickerInput value.
                let peoplePickerInput = null;
                if (context.recipients && context.recipients.length > 0) {
                    peoplePickerInput = [];
                    for (const recipient of context.recipients) {
                        if (recipient.rawPersonData) {
                            peoplePickerInput.push(recipient.rawPersonData);
                        }
                    }
                }

                let inviteesToRemove = null;
                if (shareLinkSettings.inviteesToRemove && shareLinkSettings.inviteesToRemove.length > 0) {
                    inviteesToRemove = [];
                    for (const inviteeToRemove of shareLinkSettings.inviteesToRemove) {
                        inviteesToRemove.push({
                            id: inviteeToRemove.id,
                            loginName: inviteeToRemove.loginName,
                            name: inviteeToRemove.primaryText,
                            isExternal: inviteeToRemove.isExternal,
                            principalType: inviteeToRemove.type,
                            email: inviteeToRemove.email,
                        });
                    }
                }
                // Format request body.
                let body = {
                    request: {
                        createLink: true,
                        settings: {
                            allowAnonymousAccess: shareLinkSettings.allowAnonymousAccess,
                            linkKind: shareLinkSettings.sharingLinkKind,
                            expiration: expirationString,
                            role: shareLinkSettings.role,
                            restrictShareMembership: shareLinkSettings.restrictShareMembership,
                            updatePassword: shareLinkSettings.updatePassword,
                            password: shareLinkSettings.password,
                            shareId: shareLinkSettings.shareId
                        }
                    }
                };

                if (inviteesToRemove) {
                    body.request.settings['inviteesToRemove'] = { results: inviteesToRemove };
                }

                // Include email notification fields if they're present, otherwise omit them
                // from the request.
                if (peoplePickerInput && peoplePickerInput.length > 0) {
                    body.request['peoplePickerInput'] = JSON.stringify(peoplePickerInput);

                    // A blank message is OK, but if value is null, don't append to request.
                    const emailMessage = context.emailData;
                    const emailSubject = context.emailSubject;
                    if (emailMessage !== undefined && emailSubject === undefined) {
                        body.request['emailData'] = {
                            body: context.emailData,
                            subject: ""
                        };
                    } if (emailMessage === undefined && emailSubject !== undefined) {
                        body.request['emailData'] = {
                            body: "",
                            subject: context.emailSubject
                        };
                    } if (emailMessage !== undefined && emailSubject !== undefined) {
                        body.request['emailData'] = {
                            body: context.emailData,
                            subject: context.emailSubject
                        };
                    }
                }

                // Include the share ID if one was provided.
                const shareId = context.settings.shareId;
                if (shareId) {
                    body.request.settings['shareId'] = shareId;
                    body.request['emailData'] = {
                        body: ''
                    };
                }

                return JSON.stringify(body);
            },
            /* method */ 'POST',
            /* additionalHeaders */ null,
            /* contentType */ null,
            /* maxRetries */ null,
            /* noRedirect */ true,
            /* crossSiteCollectionCall */ undefined,
            /* qosHandler */ undefined,
            /* customQosLogger */ undefined,
            /* qosExtraData */ this._getExtraData()
        ).catch((error: any) => {
            let debugMessage = '';
            try {
                debugMessage = JSON.stringify(error);
            } catch (e) {
                // do nothing
            }
            return {
                error: Error.generic,
                debugErrorMessage: debugMessage
            };
        });
    }

    public getClientId(): ClientId {
        const webTemplate = Number(this._pageContext.webTemplate);

        switch (webTemplate) {
            case WebTemplateType.teamSite:
            case WebTemplateType.group:
            case WebTemplateType.subgroup:
            case WebTemplateType.sitePagePublishing:
                return ClientId.sharePoint;
            case WebTemplateType.mySite:
                return ClientId.odb;
            default:
                return ClientId.unknown;
        }
    }

    private _shareLink(people: Array<IPerson>, context: ISharingContext<ISPListItem>): Promise<IShareResult> {
        let item: ISPListItem = context.items[0];
        let urlBuilder = this._sharingDataSourceHelper.getApiRoot(this._apiUrlHelper, item, this._getParentListId(item));
        let url: string = urlBuilder
            .segment('ShareLink')
            .toString();

        return this.getData<IShareResult>(
            /* getUrl */(): string => url,
            /* parseResponse */(responseText: string): any => {
                return <IShareResult>{
                    statusCode: 0,
                    errorMessage: '',
                    people: people,
                    itemId: context.items[0].id
                };
            },
            /* qosName */ 'ShareLink',
            /* getAdditionalPostData */(): string => {
                let peoplePickerSchema: Array<any> = [];
                for (let person of people) {
                    peoplePickerSchema.push(person.rawPersonData);
                }
                let expirationString = null;
                if (context.expiration) {
                    expirationString = convertDateToISOString(context.expiration);
                }

                let body = {
                    request: {
                        peoplePickerInput: JSON.stringify(peoplePickerSchema),
                        emailData: {
                            body: context.message,
                            subject: context.subject
                        },
                        linkKind: this._getSharingLinkKind(context.linkType, context.roleValue),
                        createLink: true,
                        expiration: expirationString
                    }
                };

                return JSON.stringify(body);
            }
        );
    }

    private _getSharingLinkKind(linkType: SharingLinkType, roleValue: string): SharingLinkKindV3 {
        let linkKind: SharingLinkKindV3 = SharingLinkKindV3.direct;
        if (linkType === SharingLinkType.Edit) {
            linkKind = SharingLinkKindV3.anonymousEdit;
        } else if (linkType === SharingLinkType.View) {
            linkKind = SharingLinkKindV3.anonymousView;
        } else if (linkType === SharingLinkType.Organization) {
            if (roleValue === SharingRole.Edit.toString()) {
                linkKind = SharingLinkKindV3.organizationEdit;
            } else {
                linkKind = SharingLinkKindV3.organizationView;
            }
        }
        return linkKind;
    }

    // Functions below here are helpers for share UI v3 data layer.

    private _parseGetSharingInformationResponse(rawGetSharingInformationResponse: IGetSharingInformationResponse, item: ISPListItem): ISharingInformation {
        // Determine if there was an error getting sharing information.
        if (rawGetSharingInformationResponse.error) {
            return {
                error: Error.generic,
                anonymousLinkExpirationRestrictionDays: undefined,
                canAddExternalPrincipal: undefined,
                canAddInternalPrincipal: undefined,
                canManagePermissions: undefined,
                defaultSharingLink: undefined,
                isShared: undefined,
                item: undefined,
                sharingLinks: undefined,
                sharingPrincipals: undefined,
                peoplePickerSettings: undefined,
                userDisplayName: undefined,
                blockPeoplePickerAndSharing: undefined,
                userRole: undefined,
                defaultShareLinkPermission: undefined,
                sharingAbilities: undefined,
                defaultLinkKind: undefined,
                directLink: undefined,
                hasPermissionsInformation: undefined,
                sharingLinkTemplates: undefined,
                webTemplateId: undefined,
                itemUniqueId: undefined
            };
        }

        const response = rawGetSharingInformationResponse.d;
        const hasPermissionsInformation = !!response.permissionsInformation;
        const hasSharingLinkTemplatesInformation = !!response.sharingLinkTemplates;

        // Get all sharing links.
        const sharingLinks = hasPermissionsInformation ? this._parseSharingLinks(response.permissionsInformation.links.results, response.directUrl) : [];

        const sharingLinkTemplates = hasSharingLinkTemplatesInformation ? this._parseSharingLinkTemplates(response.sharingLinkTemplates.templates.results) : [];

        // Get sharing principals.
        const sharingPrincipalsFromServer = hasPermissionsInformation ? response.permissionsInformation.siteAdmins.results.concat(response.permissionsInformation.principals.results) : [];
        const sharingPrincipals = this._parseSharingPrincipals(sharingPrincipalsFromServer);

        // Get default sharing link (the link that the UI will default to).
        const defaultSharingLink = this._getDefaultSharingLink(response.defaultLinkKind, sharingLinks, response.sharingAbilities);

        // Get item information if we don't have a resolved item.
        let itemInformation: ISharingItemInformation = {
            name: this._sharingContextInformation ? this._sharingContextInformation.itemName : undefined,
            childCount: this._sharingContextInformation ? this._sharingContextInformation.itemCount : undefined,
            hasDlpPolicy: this._sharingContextInformation ? this._sharingContextInformation.hasDlpPolicy : undefined,
            appmap: this._sharingContextInformation ? this._sharingContextInformation.itemAppmap : undefined
        };

        // Get item information if we have a resolved item.
        if (item) {
            const itemProperties = item.properties;
            const itemChildCount = itemProperties.ItemChildCount ? parseInt(itemProperties.ItemChildCount, 10) : 0;
            const folderChildCount = itemProperties.FolderChildCount ? parseInt(itemProperties.FolderChildCount, 10) : 0;
            const totalItemCount = item.type === ItemType.OneNote ? 0 : itemChildCount + folderChildCount;
            const blockSharingPolicyTipFlag = this._isPolicyTipFlagSet(item.policyTip, PolicyTipType.blockSharing);
            itemInformation = {
                name: item.name,
                childCount: totalItemCount,
                hasDlpPolicy: blockSharingPolicyTipFlag,
                appmap: item.appMap
            };
        }

        // Determine if user has ability to manage permissions.
        const canManagePermissions = response.canRequestAccessForGrantAccess || response.canAddInternalPrincipal || sharingLinks.length > 1;

        // On-prem protection. Probably only affects dev boxes right now...
        const anonymousLinkExpirationRestrictionDays = response.anonymousLinkExpirationRestrictionDays === 0 ? -1 : response.anonymousLinkExpirationRestrictionDays;

        // Compile all of the parsed information and return.
        const parsedResponse: ISharingInformation = {
            anonymousLinkExpirationRestrictionDays: anonymousLinkExpirationRestrictionDays,
            canAddExternalPrincipal: response.canAddExternalPrincipal,
            canAddInternalPrincipal: response.canAddInternalPrincipal,
            canManagePermissions: canManagePermissions,
            defaultSharingLink: defaultSharingLink,
            isShared: this._computeIsShared(sharingLinks, sharingPrincipals),
            item: itemInformation,
            sharingLinks: sharingLinks,
            sharingPrincipals: sharingPrincipals,
            peoplePickerSettings: response.pickerSettings,
            userDisplayName: this._pageContext.userDisplayName,
            blockPeoplePickerAndSharing: response.blockPeoplePickerAndSharing,
            requiresAccessApproval: (!response.canAddInternalPrincipal && response.canRequestAccessForGrantAccess),
            userRole: response.currentRole,
            defaultShareLinkPermission: response.defaultShareLinkPermission,
            sharingAbilities: response.sharingAbilities,
            defaultLinkKind: response.defaultLinkKind,
            directLink: {
                allowsAnonymousAccess: false,
                audience: SharingAudience.existing,
                expiration: null,
                description: null,
                isActive: true,
                isEdit: true,
                principals: [],
                invitees: [],
                shareId: '',
                sharingLinkKind: SharingLinkKindV3.direct,
                url: response.directUrl,
                updatePassword: false,
                password: '',
                passwordLastModifiedBy: null
            },
            hasPermissionsInformation: !!response.permissionsInformation,
            sharingLinkTemplates: sharingLinkTemplates,
            webTemplateId: response.webTemplateId,
            itemUniqueId: response.itemUniqueId,
            customizedExternalSharingServiceUrl: response.customizedExternalSharingServiceUrl || ''
        };

        return parsedResponse;
    }

    private _computeIsShared(sharingLinks: Array<ISharingLinkV3>, sharingPrincipals: Array<ISharingPrincipal>) {
        let isShared = false;

        // See if there are any active sharing links.
        for (const sharingLink of sharingLinks) {
            if (sharingLink.isActive && sharingLink.sharingLinkKind !== SharingLinkKindV3.direct) {
                isShared = true;
            }
        }

        // If there are no active links, see if anyone has been ACL'ed to item.
        if (!isShared) {
            isShared = sharingPrincipals.length > 0;
        }

        return isShared;
    }

    private _parseSharingLinks(links: Array<ISharingLinkFromAPI>, canonicalLink: string): Array<ISharingLinkV3> {
        // Remove inherited links.
        links = links.filter((link: ISharingLinkFromAPI) => {
            return !link.isInherited;
        });

        // Convert server response to ISharingLinkV3 interface.
        let sharingLinks: Array<ISharingLinkV3> = links.map((link: ISharingLinkFromAPI) => {
            const details = link.linkDetails;

            const sharingLink: ISharingLinkV3 = {
                allowsAnonymousAccess: details.AllowsAnonymousAccess,
                hasPassword: details.RequiresPassword,
                blockDownload: details.BlocksDownload,
                description: details.Description,
                audience: getSharingLinkAudience(details.LinkKind, details.AllowsAnonymousAccess, details.RestrictedShareMembership),
                expiration: details.Expiration ? new Date(details.Expiration) : null,
                isActive: details.IsActive,
                isEdit: computeIsEditLink(details.LinkKind) || details.IsEditLink,
                isReview: details.IsReviewLink,
                principals: this._parseSharingPrincipals(link.linkMembers.results, details.LinkKind),
                invitees: this._parseLinkInvitees(details),
                shareId: details.ShareId,
                sharingLinkKind: details.LinkKind,
                url: details.Url,
                updatePassword: details.UpdatePassword,
                password: details.Password,
                passwordLastModifiedBy: this._parseSharingPrincipal(details.PasswordLastModifiedBy),
                isCreateOnlyLink: details.IsCreateOnlyLink
            };

            return sharingLink;
        });

        // Add direct link to sharing links collection.
        sharingLinks.push({
            allowsAnonymousAccess: false,
            audience: SharingAudience.specificPeople,
            expiration: null,
            description: null,
            isActive: true,
            isEdit: true,
            principals: [],
            invitees: [],
            shareId: '',
            sharingLinkKind: SharingLinkKindV3.direct,
            url: canonicalLink,
            updatePassword: false,
            password: '',
            passwordLastModifiedBy: null
        });

        return sharingLinks;
    }

    /**
     * Parses link templates and returns as a list
     * @param links
     * @param canonicalLink
     */
    private _parseSharingLinkTemplates(links: Array<ISharingLinkTemplateFromAPI>): Array<ISharingLinkTemplate> {
        // Convert server response to ISharingLinkV3 interface.
        let sharingLinkTemplates: Array<ISharingLinkTemplate> = links.map((link: ISharingLinkTemplateFromAPI) => {
            const details = link.linkDetails;

            let parsedLinkDetails: ISharingLinkV3;

            if (details !== null) {
                parsedLinkDetails = {
                    allowsAnonymousAccess: details.AllowsAnonymousAccess,
                    hasPassword: details.RequiresPassword,
                    blockDownload: details.BlocksDownload,
                    audience: getSharingLinkAudience(details.LinkKind, details.AllowsAnonymousAccess, details.RestrictedShareMembership),
                    expiration: details.Expiration ? new Date(details.Expiration) : null,
                    description: details.Description,
                    isActive: details.IsActive,
                    isEdit: computeIsEditLink(details.LinkKind) || details.IsEditLink,
                    isReview: details.IsReviewLink,
                    principals: undefined,
                    invitees: this._parseLinkInvitees(details),
                    shareId: details.ShareId,
                    sharingLinkKind: details.LinkKind,
                    url: details.Url,
                    updatePassword: details.UpdatePassword,
                    password: details.Password,
                    passwordLastModifiedBy: this._parseSharingPrincipal(details.PasswordLastModifiedBy)
                };
            }
            const sharingLinkTemplate: ISharingLinkTemplate = {
                linkDetails: parsedLinkDetails,
                passwordProtected: link.passwordProtected,
                role: link.role,
                scope: link.scope,
                shareKind: link.shareKind
            };
            return sharingLinkTemplate;
        });

        return sharingLinkTemplates;
    }

    private _parseLinkInvitees(serverLinkDetails: any): Array<ISharingPrincipal> {
        const invitations = serverLinkDetails.Invitations;

        // If there aren't any invitations on the link, return an empty array.
        if (!invitations) {
            return [];
        }

        const invitees = [];
        for (const invitation of invitations.results) {
            if (invitation && invitation.invitee) {
                invitees.push(invitation.invitee);
            }
        }

        return invitees.map((invitee: any) => {
            return {
                id: invitee.id,
                email: invitee.email,
                primaryText: invitee.name,
                isExternal: invitee.isExternal,
                loginName: invitee.loginName,
                role: null,
                secondaryText: invitee.jobTitle,
                type: invitee.principalType,
                imageUrl: this._getImageUrl(invitee.email)
            };
        });
    }

    private _parseSharingPrincipals(principals: Array<ISharingPrincipalFromAPI>, sharingLinkKind?: SharingLinkKindV3): Array<ISharingPrincipal> {
        // Convert server response to ISharingPrincipal interface.
        let sharingPrincipals: Array<ISharingPrincipal> = principals.map((principal: ISharingPrincipalFromAPI | any) => {
            // Principals on linkMembers different from principals at root level.
            let principalInfo = principal.principal;
            if (!principalInfo) {
                principalInfo = principal;
            }

            const sharingPrincipal: ISharingPrincipal = {
                id: principalInfo.id,
                isExternal: principalInfo.isExternal,
                loginName: principalInfo.loginName,
                primaryText: principalInfo.name,
                role: principal.role,
                secondaryText: principalInfo.jobTitle,
                type: principalInfo.principalType,
                imageUrl: this._getImageUrl(principalInfo.email),
                email: principalInfo.email,
            };

            // If parsing principals from link members of a sharing link, add the sharing link
            // type from which they have access to the principal.
            if (sharingLinkKind) {
                sharingPrincipal.sharingLinkKind = sharingLinkKind;
            }

            return sharingPrincipal;
        });

        return sharingPrincipals;
    }

    private _getExtraData(): { clientId: string, mode: string } | undefined {
        let extraData: { clientId: string, mode: string } | undefined;
        if (this._sharingContextInformation) {
            extraData = {
                clientId: this._sharingContextInformation.clientId,
                mode: Mode[this._sharingContextInformation.mode]
            };
        }
        return extraData;
    }

    /**
     * Parses a ISharingPrincipalFromAPI object and returns ISharingPrincipal.
     * @param principal the principal returned from the API.
     */
    private _parseSharingPrincipal(principal: ISharingPrincipalFromAPI): ISharingPrincipal {
        if (principal) {
            let principalInfo: any = principal.principal;

            if (!principalInfo) {
                principalInfo = principal;
            }

            const sharingPrincipal: ISharingPrincipal = {
                id: principalInfo.id,
                isExternal: principalInfo.isExternal,
                loginName: principalInfo.loginName,
                primaryText: principalInfo.name,
                role: principal.role,
                secondaryText: principalInfo.jobTitle,
                type: principalInfo.principalType,
                imageUrl: this._getImageUrl(principalInfo.email),
                email: principalInfo.email,
            };

            return sharingPrincipal;
        }

        return null;
    }

    private _getDefaultSharingLink(defaultLinkKind: SharingLinkKindV3, sharingLinks: Array<ISharingLinkV3>, sharingAbilities: ISharingLinkAbilitiesMatrix): ISharingLinkV3 {
        const peopleSharingLinks = sharingAbilities.peopleSharingLinkAbilities.canManageEditLink.enabled || sharingAbilities.peopleSharingLinkAbilities.canManageReadLink.enabled;

        if (peopleSharingLinks &&
            defaultLinkKind === SharingLinkKindV3.direct) {
            defaultLinkKind = SharingLinkKindV3.flexible;
        }

        /**
         * Using "sharing abilities", then all we need to get is the default
         * link kind. The default link kind might be more permissive than the user
         * has the ability to create, so check that here and adjust accordingly.
         */
        let mostPermissiveLinkKind = defaultLinkKind;

        if (mostPermissiveLinkKind === SharingLinkKindV3.anonymousEdit && !sharingAbilities.anonymousLinkAbilities.canManageEditLink.enabled) {
            mostPermissiveLinkKind = SharingLinkKindV3.anonymousView;
        }

        if (mostPermissiveLinkKind === SharingLinkKindV3.anonymousView && !sharingAbilities.anonymousLinkAbilities.canManageReadLink.enabled) {
            mostPermissiveLinkKind = SharingLinkKindV3.organizationEdit;
        }

        if (mostPermissiveLinkKind === SharingLinkKindV3.organizationEdit && !sharingAbilities.organizationLinkAbilities.canManageEditLink.enabled) {
            mostPermissiveLinkKind = SharingLinkKindV3.organizationView;
        }

        if (mostPermissiveLinkKind === SharingLinkKindV3.organizationView && !sharingAbilities.organizationLinkAbilities.canManageReadLink.enabled) {
            mostPermissiveLinkKind = SharingLinkKindV3.direct;
        }

        return {
            allowsAnonymousAccess: undefined,
            audience: undefined,
            expiration: undefined,
            description: undefined,
            isActive: true,
            isEdit: undefined,
            principals: undefined,
            shareId: undefined,
            sharingLinkKind: mostPermissiveLinkKind,
            url: undefined,
            invitees: undefined,
            updatePassword: false,
            password: '',
            passwordLastModifiedBy: undefined
        };
    }

    private _getImageUrl(email: string): string {
        if (email && this._sharingContextInformation) {
            const clientId = this._sharingContextInformation && this._sharingContextInformation.clientId;
            if (clientId === 'word' || clientId === 'powerpoint' || clientId === 'excel') {
                return null;
            }
            return email && this._sharingContextInformation ? this._sharingContextInformation.webAbsoluteUrl + getUserPhotoUrl({ userLoginName: email }) : null;
        } else if (email && this._pageContext) {
            let webServerRelativeUrl = getSafeWebServerRelativeUrl(this._pageContext);
            return webServerRelativeUrl + getUserPhotoUrl({ userLoginName: email });
        } else {
            return null;
        }
    }

    /**
     * This function copied from PolicyTipHelper in odsp-next.
     * If we need additional helper functions from that file, we might consider a separate helper file.
     */
    private _isPolicyTipFlagSet(policyTip: PolicyTipType, flag: PolicyTipType): boolean {
        /* tslint:disable: no-bitwise */
        return policyTip && ((policyTip & flag) === flag);
        /* tslint:enable: no-bitwise */
    }
}

export function parseSharingLinkInfo(response: any) {
    if (response.d) {
        const sharingLinkInfo: any = response.d.ShareLink.sharingLinkInfo;
        const expiration = sharingLinkInfo.Expiration ? new Date(sharingLinkInfo.Expiration) : undefined;
        const sharingLink: ISharingLinkV3 = {
            allowsAnonymousAccess: sharingLinkInfo.AllowsAnonymousAccess,
            audience: getSharingLinkAudience(sharingLinkInfo.LinkKind, sharingLinkInfo.AllowsAnonymousAccess, sharingLinkInfo.RestrictedShareMembership),
            expiration: expiration,
            description: sharingLinkInfo.Description,
            isActive: sharingLinkInfo.IsActive,
            isEdit: computeIsEditLink(sharingLinkInfo.LinkKind) || sharingLinkInfo.IsEditLink,
            principals: null,
            invitees: null,
            shareId: sharingLinkInfo.ShareId,
            sharingLinkKind: sharingLinkInfo.LinkKind,
            url: sharingLinkInfo.Url,
            updatePassword: sharingLinkInfo.UpdatePassword,
            password: sharingLinkInfo.Password,
            passwordLastModifiedBy: sharingLinkInfo.PasswordLastModifiedBy
        };

        return {
            link: sharingLink
        };
    }
}

function getSharingLinkAudience(sharingLinkKind: SharingLinkKindV3, AllowsAnonymousAccess: boolean, RestrictedShareMembership: boolean): SharingAudience {
    /**
     * Note: Currently, the only flexible sharing link supported by the share UI
     * is PSLs, so we can safely return "specificPeopleFlex" as the audience. In the
     * future, if share UI supports more flexible link types, this will likely
     * need to be changed.
     */

    if (sharingLinkKind === SharingLinkKindV3.anonymousEdit || sharingLinkKind === SharingLinkKindV3.anonymousView) {
        return SharingAudience.anyone;
    } else if (sharingLinkKind === SharingLinkKindV3.organizationEdit || sharingLinkKind === SharingLinkKindV3.organizationView) {
        return SharingAudience.organization;
    } else if (sharingLinkKind === SharingLinkKindV3.direct) {
        return SharingAudience.specificPeople;
    } else if (sharingLinkKind === SharingLinkKindV3.flexible) {
        // Additional checks to make sure that we get the Audience right
        if (AllowsAnonymousAccess) {
            return SharingAudience.anyone;
        } else if (RestrictedShareMembership === false) {
            return SharingAudience.organization;
        } else {
            return SharingAudience.specificPeopleFlex;
        }
    }
}

/**
 * Determines if link has edit capabilities or not.
 * Returns null if link type is flexible (because we don't know).
 */
function computeIsEditLink(sharingLinkKind: SharingLinkKindV3): boolean {
    if (sharingLinkKind === SharingLinkKindV3.anonymousEdit || sharingLinkKind === SharingLinkKindV3.organizationEdit) {
        return true;
    } else if (sharingLinkKind === SharingLinkKindV3.anonymousView || sharingLinkKind === SharingLinkKindV3.organizationView) {
        return false;
    } else {
        // Future-proofing for flexible links...
        return null;
    }
}